const EventEmitter = require('events')
const gql = require('graphql-tag')

const backendClient = require('../../sci/helpers/client')
const createChannel = require('./channel')
const defer = require('microsistec/defer')
const guid = require('../helpers/guid')
const x9 = require('microsistec/x9')

class BusSubscription {
    /**
     * @param eventId
     * @param bus {Bus}
     */
    constructor(eventId, bus) {
        this._bus = bus
        this._handlers = []

        this._receive = this._receive.bind(this)

        bus.on(eventId, this._receive)
    }

    _receive (event, message) {
        this._handlers.forEach(fn => fn(event, message))
    }

    /**
     * @param cb
     * @returns {function(...[*]=)}
     */
    subscribe(cb) {
        const self = this

        self._handlers.push(cb)

        return {
            unsubscribe () {
                self._handlers.filter(_ => _ !== cb)
            }
        }
    }
}

class Bus {
    constructor() {
        this._channel = defer()
        this._emitter = new EventEmitter()
    }

    registerEventType (name, graphQLFragment) {
        if (graphQLFragment.kind !== "Document") {
            throw new Error("You should use `gql` tag from graphql-tag")
        }

        const definitions = graphQLFragment.definitions

        if (!Array.isArray(definitions)) {
            throw new Error("At least one fragment definition expected")
        }

        if (definitions.length !== 1) {
            throw new Error(`Only one fragment definition allowed, got ${definitions.length}`)
        }

        const definition = definitions[0]

        if (!definition) {
            throw new Error("At least one fragment definition expected")
        }

        if (definition.kind !== "FragmentDefinition") {
            throw new Error(`At least one fragment definition expected, got ${definition.kind}`)
        }

        const graphQLType = definition.name.value

        this.emit('register-event-type', { name, graphQLType, graphQLFragment })

        return new BusSubscription(name, this)
    }

    status () {
        return this.ask('are-you-alive', null, 10000).then((res) => {
            return !!res
        }).catch((ex) => {
            return false
        })
    }

    dispatch () {
        throw new Error('Use emit instead')
    }

    broadcast () {
        throw new Error('Use emit instead')
    }

    ask (eventId, content = undefined, timeout = 0) {
        return this._channel.then((channel) => {
            return channel.ask(eventId, content, timeout)
        })
    }

    emit (eventId, content = undefined) {
        return this._channel.then((channel) => {
            channel.emit(eventId, content)
        })
    }

    on (eventId, cb) {
        this._emitter.on(eventId, cb)
    }

    off (eventId, cb) {
        this._emitter.off(eventId, cb)
    }

    /**
     * @param channel {Channel}
     */
    setChannel(channel) {
        channel.onAny(({ type, message }) => {
            this._emitter.emit(type, message.content.event, message.content.message)
        })
        this._channel.resolve(channel)
    }
}

const currentBus = new Bus()

window.addEventListener('load', async (evt) => {
    if (!window['__sessionToken']) {
        return
    }

    const hash = window['__webpackInfo'].hash

    const since = window.__loadedAt

    let newWorker = null
    let channel = null

    try {
        if (window.SharedWorker) {
            if (IS_DEV) {
                newWorker = new SharedWorker(`/shared-worker.js?token=${window['__sessionToken']}`)
            } else {
                newWorker = new SharedWorker(`/${hash}/shared-worker.js?token=${window['__sessionToken']}`)
            }

            channel = createChannel('shared-worker', newWorker.port)

            await channel.start()
        } else {
            if (IS_DEV) {
                newWorker = new Worker(`/shared-worker.js?token=${window['__sessionToken']}`)
            } else {
                newWorker = new Worker(`/${hash}/shared-worker.js?token=${window['__sessionToken']}`)
            }

            channel = createChannel('worker', newWorker)

            await channel.start()
        }
    } catch (err) {
        x9.report(err)
        return
    }

    newWorker.onerror = (evt) => {
        x9.report(evt)
        console.error(evt)
    }

    await channel.emit('loaded-at', since)

    await channel.emit('client-alive', {
        meta: {
            path: window.location.pathname,
            loadedAt: window.__loadedAt,
        },
        id: channel.getId(),
        environment: {
            instanceCode: window['__instanceCode'],
            sessionToken: window['__sessionToken'],
            version: window['__version'],
            isDevelopment: window['__isDevelopment'],
            name: window['__name'],
            slugname: window['__slugname'],
            root: window['__root'],
            backendRoot: window['__backendRoot'],
            searchEndpoint: window['__searchEndpoint'],
            matrixEndpoint: window['__matrixEndpoint'],
            mailmanEndpoint: window['__mailmanEndpoint'],
            reportsEndpoint: window['__reportsEndpoint'],
            inboxesEndpoint: window['__inboxesEndpoint'],
            notificationsEndpoint: window['__notificationsEndpoint'],
            webpackInfo: window['__webpackInfo'],
            general: window['__general'],
            session: window['SESSION_DATA'],
            userIsUsingAdblockOrRelated: window.__userIsUsingAdblockOrRelated
        }
    })

    setTimeout(() => currentBus.setChannel(channel), 1000)

    currentBus.registerEventType('ack', gql`
        fragment AckEvent on AckEvent {
            name
        }
    `)

    const requestRuntimeSessionSub = currentBus.registerEventType('request-runtime-session', gql`
        fragment RequestRuntimeSessionEvent on RequestRuntimeSessionEvent {
            name
        }
    `)

    requestRuntimeSessionSub.subscribe(() => {
        const sci = document.SCI_DATA || {}
        const date = moment()
        const connection = {}

        if (navigator.connection) {
            connection.downlink = navigator.connection.downlink
            connection.effectiveType = navigator.connection.effectiveType
            connection.rtt = navigator.connection.rtt
        }

        const data = {
            online: navigator.onLine,
            instance: sci.instance,
            user: sci.user,
            token: sci.token,
            url: location.href,
            loadedAt: window.__loadedAt,
            date: {
                date: date.format('YYYY-MM-DD HH:mm:ss'),
                offset: date.utcOffset()
            },
            connection,
            adblockEnabled: window.__userIsUsingAdblockOrRelated,
            navigator: {
                deviceMemory: navigator.deviceMemory,
                appCodeName: navigator.appCodeName,
                appName: navigator.appName,
                appVersion: navigator.appVersion,
                cookieEnabled: navigator.cookieEnabled,
                hardwareConcurrency: navigator.hardwareConcurrency,
                language: navigator.language,
                languages: navigator.languages,
                platform: navigator.platform,
                product: navigator.product,
                productSub: navigator.productSub,
                userAgent: navigator.userAgent,
                vendor: navigator.vendor,
                vendorSub: navigator.vendorSub
            },
            session_data: sci.session_data,
            build_name: window.__name,
            build_slug_name: window.__slugname,
            errors: window.__rescueErrors,
            log: window.__rescueLogs
        }

        const type = 'data:application/json'

        const dataString = JSON.stringify(data)

        const blob = new Blob([dataString], { type: type })

        const formData = new FormData()

        formData.append('runtime', blob)

        backendClient.silent().post(null, '/utils/store-runtime-session', formData)
    })
})

const _pushState = history.pushState

history.pushState = async function () {
    _pushState.apply(history, arguments)

    await currentBus.emit('client-updated', {
        meta: {
            path: window.location.pathname
        }
    })
}

window.addEventListener('popstate', async function () {
    await currentBus.emit('client-updated', {
        meta: {
            path: window.location.pathname
        }
    })
})

window.addEventListener('beforeunload', async function (evt) {
    await currentBus.emit('client-dead', 1)
})

module.exports = currentBus


// WEBPACK FOOTER //
// ./resources/assets/javascript/sci/std/underworld.js